import { create } from "zustand";
import { ICategory, IPlaylist } from "@/lib/feed-api";
import { persist } from "zustand/middleware";

interface CurrentSelection {
  category: ICategory;
  playlist: IPlaylist;
}

interface CategoryStore {
  categories: ICategory[] | null;
  setCategories: (categories: ICategory[]) => void;

  currentSelection: CurrentSelection | null;
  setCurrentSelection: (categoryId: string, playlistId: string) => void;
  resetCurrentSelection: () => void;
  resetStore: () => void;
  getPlaylistById: (playlistId: string) => IPlaylist | null;
  updateTutorialCompletion: (tutorialId: string, isCompleted?: boolean) => void;
  updateTutorialPref: (tutorialId: string, isUninterested?: boolean) => void;

  getTutorialCountByPlaylist: (playlistId: string) => number;
  getCompletedTutorialCountByPlaylist: (playlistId: string) => number;
}

export const useCategoryStore = create<CategoryStore>()(
  persist(
    (set, get) => ({
      categories: [],
      currentSelection: null,

      setCategories: (categories) => set({ categories }),

      setCurrentSelection: (categoryId, playlistId) => {
        const categories = get().categories;
        if (!categories) return;

        const category = categories.find((c) => c.documentId === categoryId);
        if (!category) return;

        const playlist = category.playlists.find(
          (p) => p.documentId === playlistId
        );
        if (!playlist) return;

        set({
          currentSelection: {
            category,
            playlist,
          },
        });
      },

      resetCurrentSelection: () => {
        set({ currentSelection: null });
      },
      resetStore: () => {
        set({
          categories: [],
          currentSelection: null,
        });
      },
      getPlaylistById: (playlistId) => {
        const categories = get().categories;
        if (!categories) return null;

        for (const category of categories) {
          const playlist = category.playlists.find(
            (p) => p.documentId === playlistId
          );
          if (playlist) return playlist;
        }
        return null;
      },

      getTutorialCountByPlaylist: (playlistId) => {
        const playlist = get().getPlaylistById(playlistId);
        if (!playlist || !playlist.tutorials) return 0;
        const interestedList = playlist?.tutorials.filter(
          (v) => !v.is_uninterested
        );
        return interestedList.length ?? 0;
      },

      getCompletedTutorialCountByPlaylist: (playlistId) => {
        const playlist = get().getPlaylistById(playlistId);
        return playlist?.tutorials?.filter((t) => t.is_completed).length ?? 0;
      },

      updateTutorialCompletion: (tutorialId: string, isCompleted?: boolean) => {
        set((state) => ({
          categories: state.categories?.map((category) => ({
            ...category,
            playlists: category.playlists.map((playlist) => ({
              ...playlist,
              tutorials: playlist.tutorials.map((tutorial) =>
                tutorial.documentId === tutorialId
                  ? {
                      ...tutorial,
                      is_completed:
                        isCompleted === undefined
                          ? !tutorial.is_completed
                          : isCompleted,
                    }
                  : tutorial
              ),
            })),
          })),
        }));
      },

      updateTutorialPref: (tutorialId: string, isUninterested?: boolean) => {
        set((state) => ({
          categories: state.categories?.map((category) => ({
            ...category,
            playlists: category.playlists.map((playlist) => ({
              ...playlist,
              tutorials: playlist.tutorials.map((tutorial) =>
                tutorial.documentId === tutorialId
                  ? {
                      ...tutorial,
                      is_uninterested:
                        isUninterested === undefined
                          ? !tutorial.is_uninterested
                          : isUninterested,
                    }
                  : tutorial
              ),
            })),
          })),
        }));
      },
    }),
    {
      name: "category-store",
      partialize: (state) => ({
        currentSelection: state.currentSelection,
      }),
    }
  )
);
